import { useEffect, useRef, useState } from "react";
import { useTheme } from "../../../contexts/ThemeContext";
import EarlyAccessSignupModal from "../../../components/feature/EarlyAccessSignupModal";

const FEATURES = [
  {
    icon: "ri-calculator-line",
    title: "True Profit Calculator",
    description:
      "Enter your buy price, sale price, platform, and shipping. Trackly instantly calculates every fee and shows you real net profit — not the number you hope it is.",
  },
  {
    icon: "ri-eye-off-line",
    title: "Hidden Fee Detection",
    description:
      "Platform fees, payment processing, shipping, and listing costs — all automatically accounted for. Nothing slips through the cracks, no matter what you're selling.",
  },
  {
    icon: "ri-percent-line",
    title: "Real ROI on Every Flip",
    description:
      "Know your return on investment before you even list. Compare deals side by side and see exactly which items and platforms are worth your time.",
  },
  {
    icon: "ri-line-chart-line",
    title: "Daily Performance Tracking",
    description:
      "See how your reselling adds up day by day, week by week. Know if you're improving, plateauing, or quietly losing ground.",
  },
  {
    icon: "ri-layout-grid-line",
    title: "Multi-Platform Support",
    description:
      "eBay, StockX, GOAT, Poshmark, Depop, Whatnot, Mercari, and more. Every platform's unique fee structure is pre-loaded — no math required.",
  },
  {
    icon: "ri-pie-chart-2-line",
    title: "Full Cost Breakdown",
    description:
      "See exactly where every dollar goes on every sale. Platform cut, shipping, payment processing — laid out clearly, every time.",
  },
];

export default function FeaturesSection() {
  const { isDark } = useTheme();
  const headerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [headerIn, setHeaderIn] = useState(false);
  const [gridIn, setGridIn] = useState(false);
  const [showSignup, setShowSignup] = useState(false);

  useEffect(() => {
    const observe = (el: HTMLElement | null, setter: (v: boolean) => void) => {
      if (!el) return () => {};
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setter(true);
            obs.unobserve(el);
          }
        },
        { threshold: 0.1 }
      );
      obs.observe(el);
      return () => obs.disconnect();
    };

    const c1 = observe(headerRef.current, setHeaderIn);
    const c2 = observe(gridRef.current, setGridIn);
    return () => { c1(); c2(); };
  }, []);

  return (
    <section
      className={`pt-10 pb-28 lg:py-28 px-8 lg:px-16 transition-colors duration-300 ${
        isDark ? "bg-[#050505]" : "bg-white"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div ref={headerRef} className="text-center mb-16">
          <span
            className={`font-inter text-xs uppercase tracking-[0.2em] block mb-4 reveal-up${headerIn ? " in-view" : ""} ${
              isDark ? "text-[#666666]" : "text-[#888888]"
            }`}
            style={{ transitionDelay: "0ms" }}
          >
            Why Trackly
          </span>
          <h2
            className={`font-syne font-bold text-5xl lg:text-6xl leading-tight reveal-up${headerIn ? " in-view" : ""} ${
              isDark ? "text-white" : "text-[#111111]"
            }`}
            style={{ transitionDelay: "100ms" }}
          >
            Stop guessing.{" "}
            <br />
            <span
              className={`text-transparent bg-clip-text ${
                isDark
                  ? "bg-gradient-to-r from-[#888888] to-[#333333]"
                  : "bg-gradient-to-r from-[#555555] to-[#cccccc]"
              }`}
            >
              Start knowing.
            </span>
          </h2>
          <p
            className={`font-inter text-lg mt-5 max-w-xl mx-auto leading-relaxed reveal-up${headerIn ? " in-view" : ""} ${
              isDark ? "text-[#555555]" : "text-[#777777]"
            }`}
            style={{ transitionDelay: "220ms" }}
          >
            No spreadsheets. No complicated accounting. Just clear, accurate
            numbers on every item you resell.
          </p>
        </div>

        {/* Grid */}
        <div
          ref={gridRef}
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px rounded-2xl overflow-hidden border ${
            isDark ? "bg-white/5 border-white/5" : "bg-black/5 border-black/5"
          }`}
        >
          {FEATURES.map((feature, i) => (
            <div
              key={feature.title}
              className={`p-8 hover:transition-colors duration-300 group cursor-default reveal-up${gridIn ? " in-view" : ""} ${
                isDark
                  ? "bg-[#050505] hover:bg-[#0d0d0d]"
                  : "bg-white hover:bg-[#f8f8f8]"
              }`}
              style={{ transitionDelay: gridIn ? `${i * 80}ms` : "0ms" }}
            >
              <div className="w-10 h-10 flex items-center justify-center mb-5">
                <i
                  className={`${feature.icon} text-2xl transition-colors duration-300 ${
                    isDark
                      ? "text-[#555555] group-hover:text-[#888888]"
                      : "text-[#999999] group-hover:text-[#555555]"
                  }`}
                />
              </div>
              <h3
                className={`font-syne font-semibold text-lg mb-3 ${
                  isDark ? "text-white" : "text-[#111111]"
                }`}
              >
                {feature.title}
              </h3>
              <p
                className={`font-inter text-sm leading-relaxed transition-colors duration-300 ${
                  isDark
                    ? "text-[#555555] group-hover:text-[#666666]"
                    : "text-[#777777] group-hover:text-[#555555]"
                }`}
              >
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Mobile-only flip calculator — sits below Full Cost Breakdown */}
        <div className={`lg:hidden mt-4 border rounded-2xl p-5 transition-colors duration-300 ${
          isDark ? "bg-[#0d0d0d] border-white/10" : "bg-[#f0f0f0] border-black/8"
        }`}>
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <span className={`font-inter text-[10px] uppercase tracking-widest ${isDark ? "text-[#666]" : "text-[#888]"}`}>
              Flip Calculator
            </span>
            <div className="flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${isDark ? "bg-[#e0e0e0]" : "bg-[#444]"}`} />
              <span className={`font-inter text-[10px] ${isDark ? "text-[#666]" : "text-[#888]"}`}>Live preview</span>
            </div>
          </div>

          {/* Inputs */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            {[
              { label: "Buy Price", value: "$45.00" },
              { label: "Sale Price", value: "$120.00" },
              { label: "Platform", value: "eBay" },
              { label: "Shipping", value: "$8.50" },
            ].map((item) => (
              <div key={item.label} className={`border rounded-xl p-3 ${isDark ? "bg-[#111] border-white/5" : "bg-white border-black/8"}`}>
                <p className={`font-inter text-[10px] mb-0.5 ${isDark ? "text-[#555]" : "text-[#888]"}`}>{item.label}</p>
                <p className={`font-inter text-sm ${isDark ? "text-[#999]" : "text-[#444]"}`}>{item.value}</p>
              </div>
            ))}
          </div>

          {/* Fee breakdown */}
          <div className={`border-t mb-4 ${isDark ? "border-white/5" : "border-black/5"}`} />
          <p className={`font-inter text-[10px] uppercase tracking-widest mb-2.5 ${isDark ? "text-[#555]" : "text-[#888]"}`}>
            Fee Breakdown
          </p>
          <div className="flex flex-col gap-2 mb-4">
            {[
              { label: "eBay Final Value Fee (13.25%)", value: "-$15.90" },
              { label: "Payment Processing (2.9%)", value: "-$3.57" },
              { label: "Shipping Cost", value: "-$8.50" },
            ].map((row) => (
              <div key={row.label} className="flex items-center justify-between">
                <span className={`font-inter text-[10px] ${isDark ? "text-[#666]" : "text-[#777]"}`}>{row.label}</span>
                <span className={`font-inter text-[10px] ${isDark ? "text-[#555]" : "text-[#888]"}`}>{row.value}</span>
              </div>
            ))}
          </div>

          {/* Result */}
          <div className={`border rounded-xl p-3.5 ${isDark ? "bg-white/4 border-white/8" : "bg-black/4 border-black/8"}`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className={`font-inter text-xs ${isDark ? "text-[#888]" : "text-[#555]"}`}>Net Profit</span>
              <span className={`font-syne font-bold text-xl ${isDark ? "text-white" : "text-[#111]"}`}>$47.03</span>
            </div>
            <div className="flex items-center justify-between">
              <span className={`font-inter text-[10px] ${isDark ? "text-[#666]" : "text-[#777]"}`}>Return on Investment</span>
              <span className={`font-inter text-xs font-medium ${isDark ? "text-[#888]" : "text-[#555]"}`}>+104.5% ROI</span>
            </div>
          </div>
        </div>

        {/* Unlock nudge banner — visible on all screen sizes */}
        <div
          className={`mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-2xl px-6 py-5 border ${
            isDark ? "bg-[#0d0d0d] border-white/8" : "bg-[#f7f7f7] border-black/6"
          }`}
        >
          <div className="flex items-center gap-4">
            <div className={`w-9 h-9 flex items-center justify-center rounded-xl shrink-0 ${
              isDark ? "bg-white/8 border border-white/10" : "bg-black/6 border border-black/8"
            }`}>
              <i className={`ri-lock-unlock-line text-base ${isDark ? "text-[#888]" : "text-[#555]"}`} />
            </div>
            <div>
              <p className={`font-syne font-semibold text-sm ${isDark ? "text-white" : "text-[#111111]"}`}>
                Use the real Flip Calculator — free with early access
              </p>
              <p className={`font-inter text-xs mt-0.5 leading-relaxed ${isDark ? "text-[#555]" : "text-[#888]"}`}>
                Sign up to instantly unlock the live calculator in your beta dashboard. Enter your own numbers, track your real flips.
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowSignup(true)}
            className={`shrink-0 font-inter font-medium text-xs px-5 py-2.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2 ${
              isDark
                ? "bg-white text-black hover:bg-[#e8e8e8]"
                : "bg-[#111111] text-white hover:bg-[#2a2a2a]"
            }`}
          >
            Get Instant Access
            <i className="ri-arrow-right-line" />
          </button>
        </div>

        {showSignup && <EarlyAccessSignupModal onClose={() => setShowSignup(false)} />}
      </div>
    </section>
  );
}
